import type { ReactElement } from 'react';
import React, { useEffect, useState } from 'react';

import { Box, Grid } from '@zillow/constellation';

import type { getOmpDataQuery } from '../../__generated__/graphql';
import { CONTAINER_SX, MOBILE_BREAKPOINT } from '../../constants/styles';
import { getRTBCards } from './constants';
import RTBCardLoaded from './RTBCardLoaded';
import RTBCardLoading from './RTBCardLoading';

const RTBCardList: React.FC<RTBCardListProps> = ({
    loading = false,
    onsiteMessageData = {},
}): ReactElement => {
    const rtbCards = getRTBCards();

    const isOmpDataComplete = rtbCards.every((card) =>
        onsiteMessageData.onsiteMessage?.messages?.some(
            (entry) => card.placementName === entry?.placementName && !!entry.selectedTreatment
        )
    );

    const [popUpNo, setPopUpNo] = useState(-1);
    useEffect(() => {
        if (loading) {
            return;
        }

        let onAppear: number;
        onAppear = rtbCards.length;
        const showUp = setInterval(() => {
            if (onAppear > 0) {
                setPopUpNo(rtbCards.length - onAppear);
                onAppear -= 1;
            } else {
                setPopUpNo(rtbCards.length);
                clearInterval(showUp);
            }
        }, 220);
    }, [loading]);

    return (
        <Box
            sx={{
                backgroundColor: '$colors.backgroundLight',
                width: '100%',
            }}
        >
            <Box
                as={Grid}
                sx={{
                    ...CONTAINER_SX,
                    [MOBILE_BREAKPOINT]: {
                        ...CONTAINER_SX[MOBILE_BREAKPOINT],
                        gridTemplateColumns: 'repeat(1, 1fr) !important',
                    },
                }}
                data-testid="hops-rtb-cards"
                display="grid"
                gridTemplateColumns={{
                    default: 'repeat(3, 1fr)',
                }}
                gap="md"
            >
                {rtbCards.map((card, index) => (
                    <React.Fragment key={card.placementName}>
                        {loading ? (
                            <RTBCardLoading />
                        ) : (
                            <RTBCardLoaded
                                card={card}
                                onsiteMessageData={onsiteMessageData}
                                additionalProps={{
                                    active: index === popUpNo,
                                    show: index <= popUpNo,
                                    zoom: { animationDuration: 200 },
                                }}
                                showFallback={!isOmpDataComplete}
                                index={index + 1}
                            />
                        )}
                    </React.Fragment>
                ))}
            </Box>
        </Box>
    );
};

interface RTBCardListProps {
    loading: boolean;
    onsiteMessageData: getOmpDataQuery | undefined;
}

export default RTBCardList;
